import { Spinner } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ApiError, getFlowOptions } from "@/api";
import { CenteredScreen } from "@/components/CenteredScreen";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";
import { FlowRunDetailPage } from "@/features/build/workflows/FlowRunDetailPage";
import { FlowRunsTab } from "@/features/build/workflows/FlowRunsTab";
import { spaceWorkflowRunPath } from "@/features/build/workflows/flowRunPaths";
import { RunFlowButton } from "@/features/build/workflows/RunFlowButton";
import { NotFoundPage } from "@/features/errors/NotFoundPage";
import { spaceCrumb } from "../spaceCrumb";
import { useSpaceHub } from "./useSpaceHub";

function useSpaceAccess(spaceUuid: string) {
  const hub = useSpaceHub(spaceUuid);
  const missing = hub.error instanceof ApiError && (hub.error.status === 404 || hub.error.status === 403);
  return { missing, crumb: spaceCrumb(spaceUuid, hub.data?.name) };
}

/**
 * `/spaces/:spaceUuid/workflows/:uuid`: a workflow used from a space (MHS-1011). Its runs and Run, kept under the
 * space (`{Space} / {Workflow}`); building it stays in Build. The workflow's own access is checked as usual.
 */
export function SpaceWorkflowPage() {
  const { t } = useTranslation("workflows");
  const { spaceUuid = "", uuid = "" } = useParams();
  const space = useSpaceAccess(spaceUuid);
  const flow = useQuery(getFlowOptions({ path: { flow_uuid: uuid } }));
  if (space.missing) return <NotFoundPage />;
  if (flow.isPending) {
    return (
      <CenteredScreen>
        <Spinner size="lg" />
      </CenteredScreen>
    );
  }
  if (flow.isError) {
    return (
      <CenteredScreen>
        <Text tone="error" role="alert">
          {t("detail.notFound")}
        </Text>
      </CenteredScreen>
    );
  }
  const data = flow.data.data;
  const runPath = (flowUuid: string, runUuid: string) => spaceWorkflowRunPath(spaceUuid, flowUuid, runUuid);
  return (
    <div className="flex flex-col">
      <PageBreadcrumbs
        trail={[space.crumb]}
        current={data.name}
        actions={<RunFlowButton flow={data} runPath={runPath} />}
      />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-6 md:px-8">
        {data.description && (
          <Text variant="body-sm" tone="secondary">
            {data.description}
          </Text>
        )}
        <FlowRunsTab flow={data} runPath={runPath} showRun={false} />
      </div>
    </div>
  );
}

/** `/spaces/:spaceUuid/workflows/:uuid/runs/:runUuid`: one run, under the space (`{Space} / {Workflow} / {run}`). */
export function SpaceWorkflowRunPage() {
  const { spaceUuid = "" } = useParams();
  const space = useSpaceAccess(spaceUuid);
  if (space.missing) return <NotFoundPage />;
  return <FlowRunDetailPage space={{ uuid: spaceUuid, crumb: space.crumb }} />;
}
